import { useField } from 'formik'
import { AnimatePresence } from 'framer-motion'
import { useTheme } from 'styled-components'

import { getDropInProps } from '@/modules/shared/animations/dropIn.animation'
import { Flex } from '../../layout'
import { AnimatedStIcon, StCheckbox, StCheckboxContainer, StLabel } from './Checkbox.styled'

import type { InputHTMLAttributes } from 'react'
import type { PartiallyRequired } from '@/utils/typescript'

type CheckboxProps = {
  label?: string
  type: 'radio' | 'checkbox'
  style?: React.CSSProperties
  checked?: boolean
  onChange?: (event?: React.ChangeEvent<HTMLInputElement>) => void
} & PartiallyRequired<Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'checked' | 'onChange'>, 'name' | 'id'>

const Checkbox = ({
  label,
  style,
  checked: controlledChecked,
  onChange: controlledOnChange,
  disabled,
  ...props
}: CheckboxProps) => {
  const theme = useTheme()
  const [field] = useField({
    ...props,
    value: props.value?.toString(), // Only string values are supported
  })

  // Support controlled mode when checked/onChange are provided
  const isChecked = controlledChecked ?? field.checked

  return (
    <Flex alignItems={'flex-start'} style={style} gap={theme.UI.Spacing.Space3}>
      <StCheckboxContainer>
        <AnimatePresence>
          {isChecked ? (
            <AnimatedStIcon
              key={props.name}
              {...getDropInProps({
                hidden: { y: 10 },
                exit: { opacity: 0, y: 10 },
              })}
              icon={['fal', 'check']}
              color={theme.Colors.Background.White}
            />
          ) : null}
        </AnimatePresence>
        <StCheckbox
          {...field}
          id={props.id}
          type={props.type}
          checked={isChecked}
          disabled={disabled}
          onChange={controlledOnChange ?? field.onChange}
        />
      </StCheckboxContainer>
      <StLabel
        htmlFor={props.id}
        style={{
          width: '100%',
          color: theme.Components.Conversational.Text,
          fontSize: '16px',
          fontWeight: 400,
        }}
      >
        {label}
      </StLabel>
    </Flex>
  )
}

export { Checkbox }
